import React from 'react'
import { IconButton, Tooltip } from '@material-ui/core'
import Svg, { ICON } from '_components/svg'
import HelpOutlineOutlinedIcon from '@material-ui/icons/HelpOutlineOutlined'
import { formatDate, displayCurrency } from '_/utils/helpers'
import { ROUTES } from '_utils/constants'

const commonColumns = [
  {
    field: 'createdAt',
    headerName: 'Criado em',
    width: 180,
    editable: false,
    renderCell: ({ value }) => (value ? formatDate(value, '') : '-'),
  },
  {
    field: 'id',
    headerName: 'ID',
    width: 100,
    editable: false,
  },
  {
    field: 'bitrixId',
    headerName: 'ID Bitrix',
    width: 150,
    editable: false,
  },
  {
    field: 'agency',
    headerName: 'Intermediária',
    flex: 1,
    editable: false,
    renderCell: ({ value }) => value?.name || '-',
  },
]

const InspectionBudgetColumns = (loading, handleDownload) => [
  ...commonColumns,
  {
    field: 'tradesman',
    headerName: 'Prestador',
    flex: 1,
    editable: false,
    renderCell: ({ value }) => value?.name || 'Refera',
  },
  {
    field: 'liquidValue',
    headerName: 'Valor total do orçamento',
    width: 200,
    editable: false,
    renderCell: ({ value }) => (value != null ? displayCurrency(Number(value)) : '-'),
  },
  {
    field: 'download',
    headerName: 'Baixar PDF',
    width: 100,
    renderCell: params => (
      <IconButton disabled={loading} onClick={event => handleDownload(event, params.row)}>
        <Svg type={ICON.DOWNLOAD} />
      </IconButton>
    ),
    sortable: false,
  },
]

const InsuranceBudgetColumns = (loading, handleDownload) => [
  ...commonColumns,
  {
    field: 'maxLiquidValue',
    headerName: 'Valor máximo',
    width: 200,
    editable: false,
    renderCell: ({ value }) => (value != null ? displayCurrency(Number(value)) : '-'),
  },

  {
    field: 'liquidValue',
    headerName: 'Valor ideal',
    width: 200,
    editable: false,
    renderCell: ({ value }) => (value != null ? displayCurrency(Number(value)) : '-'),
  },

  {
    field: 'download',
    headerName: 'Baixar PDF',
    width: 100,
    renderCell: params => (
      <IconButton disabled={loading} onClick={event => handleDownload(event, params.row)}>
        <Svg type={ICON.DOWNLOAD} />
      </IconButton>
    ),
    sortable: false,
  },
]

export const BUDGET_COLUMNS = (budgetType, loading, handleDownload) =>
  budgetType === 'inspection'
    ? InspectionBudgetColumns(loading, handleDownload)
    : InsuranceBudgetColumns(loading, handleDownload)

const normalizeUnidPk = (unid, unit) => {
  const candidate = unid?.id ?? unid?.value ?? unid ?? unit?.id ?? unit?.value ?? unit ?? null

  if (typeof candidate === 'number') return candidate
  if (typeof candidate === 'string' && /^\d+$/.test(candidate)) return Number(candidate)
  return null
}

export function normalizePayload(payload) {
  return {
    ...payload,
    agency: payload.agency?.id,
    tradesman: payload.tradesman?.id,

    inspectionBudgetCompartments: (payload?.inspectionBudgetCompartments ?? []).map(c => ({
      ...c,
      compartment: c?.compartment,

      inspectionBudgetResolutions: (c?.inspectionBudgetResolutions ?? []).map(r => ({
        ...r,
        service: r?.service?.id ?? r?.service,

        budgetResolutionMaterials: (r?.budgetResolutionMaterials ?? []).map(m => {
          const { unit, unid, ...restMaterial } = m ?? {}

          return {
            ...restMaterial,
            unid: normalizeUnidPk(unid, unit),
            total: Number(((Number(m.unitPrice) || 0) * (Number(m.quantity) || 0)).toFixed(2)),
          }
        }),
      })),
    })),
  }
}

const discountLabel = styles => {
  return (
    <span className={styles.tooltipLabel}>
      Desconto prestador
      <Tooltip title="O desconto é aplicado ao valor do prestador." arrow>
        <HelpOutlineOutlinedIcon className={styles.tooltipIcon} />
      </Tooltip>
    </span>
  )
}

export const TOTAL_VALUES = (styles, budgetType = 'inspection') => {
  const values = [
    {
      name: 'materialsTotal',
      label: 'Total materiais',
      placeholder: 'R$ 0,00',
      prefix: 'R$ ',
      style: { gridRow: '2 / 3', gridColumn: '1 / 2' },
      disabled: true,
    },
    {
      name: 'laborValueTotal',
      label: 'Total mão de obra',
      placeholder: 'R$ 0,00',
      prefix: 'R$ ',
      style: { gridRow: '2 / 3', gridColumn: '2 / 3' },
      disabled: true,
    },
    {
      name: 'totalPriceServiceProvider',
      label: 'Total do prestador',
      placeholder: 'R$ 0,00',
      prefix: 'R$ ',
      style: { gridRow: '3 / 4', gridColumn: '1 / 4' },
      disabled: true,
    },
    {
      name: 'totalProviderWithDiscount',
      label: 'Total do prestador c/ desconto',
      placeholder: 'R$ 0,00',
      prefix: 'R$ ',
      style: { gridRow: '3 / 4', gridColumn: '4 / 7' },
      disabled: true,
    },
    ...(budgetType === 'insurance'
      ? []
      : [
          {
            name: 'discount',
            label: '',
            prefix: '- R$ ',
            style: { gridRow: '2 / 3', gridColumn: '4 / 5' },
          },
          {
            name: 'priceRefera',
            label: '',
            prefix: 'R$ ',
            style: { gridRow: '2 / 3', gridColumn: '5 / 6' },
          },
          {
            name: 'priceAgency',
            label: '',
            prefix: 'R$ ',
            style: { gridRow: '2 / 3', gridColumn: '6 / 7' },
          },
          {
            name: 'percentualDiscount',
            label: discountLabel(styles),
            placeholder: '0,00 %',
            suffix: ' %',
            style: { gridColumn: '4 / 5', gridRow: '1 / 2' },
          },

          {
            name: 'percentualPriceRefera',
            label: 'Tarifa refera',
            placeholder: '0,00 %',
            suffix: ' %',
            style: { gridColumn: '5 / 6', gridRow: '1 / 2' },
          },
          {
            name: 'percentualPriceAgency',
            label: 'Tarifa intermediária',
            placeholder: '0,00 %',
            suffix: ' %',
            style: { gridColumn: '6 / 7', gridRow: '1 / 2' },
          },
        ]),
  ]
  return values
}

export const percentageToValueMap = {
  percentualPriceRefera: 'priceRefera',
  percentualPriceAgency: 'priceAgency',
  percentualDiscount: 'discount',
}

export const valueToPercentageMap = {
  priceRefera: 'percentualPriceRefera',
  priceAgency: 'percentualPriceAgency',
  discount: 'percentualDiscount',
}

export const duplicationModalHeaders = [
  { key: 'checkbox', label: '' },
  { key: 'refera', label: 'Percentual refera' },
  { key: 'agency', label: 'Percentual intermediária' },
  { key: 'tradesman', label: 'Prestador' },
  { key: 'actions', label: '' },
]

export const EXCLUDED_TYPES_EXECUTION_TIME = new Set(['courtesy', 'grouped'])

export const LABOR_TYPE_SLUGS = {
  PAINTING: 'painting',
}

export const MENU_PROPS = {
  getContentAnchorEl: null,
  anchorOrigin: {
    vertical: 'bottom',
    horizontal: 'center',
  },
  transformOrigin: {
    vertical: 'top',
    horizontal: 'center',
  },
}

export const DISCOUNT_NAMES_SET = new Set(['discount', 'percentualDiscount'])

export const LIST_TITLE = {
  inspection: 'Orçamentos de vistoria',
  insurance: 'Orçamentos de seguros',
}

export const SHOW_PROVIDERS_BUTTON = {
  inspection: true,
  insurance: false,
}

export const AGENCIES_TO_LOAD = {
  inspection: {
    handles_inspection: true,
  },
  insurance: {
    area_of_activity: 'Garantidora',
  },
}

export const MODAL_TITLE = {
  inspection: 'Dados para orçamento de Desocupação / Vistoria',
  insurance: 'Dados para orçamento de seguro',
}

export const MODAL_CONTENT = {
  inspection: 'Documento de vistoria',
  insurance: 'Documento de seguro',
}

export const MODAL_PLACEHOLDER = {
  inspection: 'Descreva aqui os detalhes da vistoria...',
  insurance: 'Descreva aqui os detalhes do seguro...',
}

export const BUDGET_URL = {
  inspection: ROUTES.INSPECTION_BUDGET,
  insurance: ROUTES.INSURANCE_BUDGET,
}

export const VIEW_TITLE = {
  inspection: 'Orçamento de vistoria',
  insurance: 'Orçamento de seguros',
}

export const LIST_ROUTES = {
  inspection: ROUTES.INSPECTION_BUDGET_LIST,
  insurance: ROUTES.INSURANCE_BUDGET_LIST,
}

export const GET_MAX_LABOR_VALUE = {
  inspection: false,
  insurance: true,
}

export const FOOTER_MAIN_VALUE_LABEL = {
  inspection: 'Valor total do orçamento',
  insurance: 'Total da imobiliária',
}
